import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/layout/AppShell";
import { EmptyState } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Network, Plus, KeyRound, Copy, ShieldAlert, RefreshCw, Trash2, Building2,
  Clock, AlertTriangle, ListChecks, CheckCircle2, XCircle, Link as LinkIcon,
} from "lucide-react";

const EMPTY = { name: "", sector: "", country: "", description: "", ciso_email: "", ciso_name: "CISO" };
const SELECT_CLS = "bg-secondary border border-border rounded-sm px-2 py-1.5 text-xs text-foreground";

const SECRET_BADGE = {
  active: { label: "Actif", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  pending: { label: "En attente", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  expired: { label: "Secret expiré", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  none: { label: "—", cls: "border-transparent text-muted-foreground" },
};

const OV_TONE = { default: "text-foreground", green: "text-green-400", blue: "text-blue-400", red: "text-red-400" };

const JOURNAL_ACTIONS = [
  ["", "Toutes les actions"],
  ["tenant.provision", "Provisionnement tenant"],
  ["tenant.activation_issued", "Émission lien d'activation"],
  ["tenant.activation_resend", "Renvoi lien d'activation"],
  ["auth.activation_complete", "Activation (lien one-time)"],
  ["auth.first_password", "Activation (secret initial)"],
  ["tenant.secret_regenerate", "Régénération secret CISO"],
  ["tenant.ghost_purge", "Purge organisation fantôme"],
];

function OvCard({ testid, icon: Icon, label, value, tone = "default" }) {
  return (
    <div className="border border-border bg-card rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`mt-2 font-head text-3xl font-bold ${OV_TONE[tone]}`}>{value ?? "—"}</div>
    </div>
  );
}

function SecretDialog({ data, onClose }) {
  if (!data) return null;
  const activationUrl = data.activation_token ? `${window.location.origin}/activate?token=${data.activation_token}` : null;
  const copyLink = () => { navigator.clipboard?.writeText(activationUrl); toast.success("Lien d'activation copié"); };
  const copySecret = () => { navigator.clipboard?.writeText(data.temp_password); toast.success("Secret copié"); };
  return (
    <Dialog open={!!data} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm" data-testid="tenant-secret-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" /> Onboarding du CISO
          </DialogTitle>
          <DialogDescription>
            Ces informations sont affichées <strong>une seule fois</strong>. Transmettez-les au CISO par un canal sûr.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="text-xs text-muted-foreground">
            Tenant : <span className="font-mono">{data.org_id}</span> · CISO : <span className="font-mono">{data.ciso_email}</span>
          </div>
          {activationUrl && (
            <div className="border border-primary/30 bg-primary/5 rounded-sm p-3">
              <div className="text-xs font-semibold text-primary mb-1 flex items-center gap-1.5">
                <LinkIcon className="h-3.5 w-3.5" /> Lien d'activation (recommandé)
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">Le CISO définit lui-même son mot de passe. Aucun secret ne circule dans le lien.</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2.5 bg-secondary rounded-sm font-mono text-xs break-all" data-testid="tenant-activation-link">{activationUrl}</code>
                <Button size="icon" variant="outline" className="rounded-sm" onClick={copyLink} data-testid="copy-activation-link-button"><Copy className="h-4 w-4" /></Button>
              </div>
              {data.activation_expires_at && (
                <p className="text-[11px] text-orange-400 mt-2 flex items-center gap-1.5">
                  <ShieldAlert className="h-3.5 w-3.5" /> Expire le {new Date(data.activation_expires_at).toLocaleString("fr-FR")}.
                </p>
              )}
            </div>
          )}
          {data.temp_password && (
            <details className="border border-border rounded-sm p-3" data-testid="legacy-secret-section">
              <summary className="text-xs text-muted-foreground cursor-pointer">Secret temporaire (compatibilité) — alternative au lien</summary>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 p-2.5 bg-secondary rounded-sm font-mono text-sm break-all" data-testid="tenant-temp-secret">{data.temp_password}</code>
                <Button size="icon" variant="outline" className="rounded-sm" onClick={copySecret} data-testid="copy-secret-button"><Copy className="h-4 w-4" /></Button>
              </div>
            </details>
          )}
        </div>
        <DialogFooter>
          <Button className="rounded-sm" onClick={onClose} data-testid="close-secret-button">J'ai noté</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TenantAdmin() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [secret, setSecret] = useState(null);
  const [ghosts, setGhosts] = useState(null);
  const [selected, setSelected] = useState({});
  const [jf, setJf] = useState({ org_id: "", action: "", result: "", date_from: "", date_to: "" });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: async () => (await api.get("/admin/tenants")).data,
  });
  const { data: overview } = useQuery({
    queryKey: ["tenants-overview"],
    queryFn: async () => (await api.get("/admin/tenants/overview")).data,
  });
  const { data: journal = [] } = useQuery({
    queryKey: ["tenants-journal", jf],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: "50" });
      if (jf.org_id) p.set("org_id", jf.org_id);
      if (jf.action) p.set("action", jf.action);
      if (jf.result) p.set("result", jf.result);
      if (jf.date_from) p.set("date_from", jf.date_from);
      if (jf.date_to) p.set("date_to", jf.date_to + "T23:59:59");
      return (await api.get(`/admin/tenants/journal?${p.toString()}`)).data;
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["tenants"] });
    qc.invalidateQueries({ queryKey: ["tenants-overview"] });
    qc.invalidateQueries({ queryKey: ["tenants-journal"] });
  };

  const provision = useMutation({
    mutationFn: async () => (await api.post("/admin/tenants", form)).data,
    onSuccess: (d) => { setOpen(false); setForm(EMPTY); setSecret(d); invalidate(); toast.success("Tenant provisionné"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const regen = useMutation({
    mutationFn: async (orgId) => (await api.post(`/admin/tenants/${orgId}/regenerate-secret`)).data,
    onSuccess: (d) => { setSecret(d); invalidate(); toast.success("Identifiants temporaires régénérés"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const resend = useMutation({
    mutationFn: async (orgId) => (await api.post(`/admin/tenants/${orgId}/resend-activation`)).data,
    onSuccess: (d) => { setSecret(d); invalidate(); toast.success("Nouveau lien d'activation généré"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const scanGhosts = useMutation({
    mutationFn: async () => (await api.get("/admin/tenants/ghost-organizations")).data,
    onSuccess: (d) => { setGhosts(d); setSelected({}); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const purge = useMutation({
    mutationFn: async (ids) => (await api.post("/admin/tenants/ghost-organizations/purge", { ids })).data,
    onSuccess: (d) => { toast.success(`${d.purged_count} organisation(s) fantôme(s) purgée(s)`); scanGhosts.mutate(); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const expiredTenants = rows.filter((t) => t.ciso_secret_status === "expired");

  return (
    <AppShell title="Gestion des tenants" subtitle="Administration PLATEFORME — provisioning multi-tenant (Global Admin)"
      actions={<Button size="sm" className="rounded-sm gap-2" onClick={() => setOpen(true)} data-testid="provision-tenant-button"><Plus className="h-4 w-4" /> Provisionner un tenant</Button>}>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="tenant-overview">
          <OvCard testid="ov-tenant-count" icon={Building2} label="Tenants" value={overview.tenant_count} />
          <OvCard testid="ov-ciso-active" icon={CheckCircle2} label="CISO actifs" value={overview.ciso_active} tone="green" />
          <OvCard testid="ov-ciso-pending" icon={Clock} label="CISO en attente" value={overview.ciso_pending} tone="blue" />
          <OvCard testid="ov-ciso-expired" icon={AlertTriangle} label="Secrets expirés" value={overview.ciso_expired} tone="red" />
        </div>
      )}

      {/* Alerte activations/secrets expirés avec action explicite de régénération */}
      {expiredTenants.length > 0 && (
        <div className="mb-6 border border-red-500/40 bg-red-500/10 rounded-sm p-4" data-testid="expired-secrets-alert">
          <div className="flex items-center gap-2 text-red-300 font-semibold">
            <AlertTriangle className="h-5 w-5" /> {expiredTenants.length} activation(s)/secret(s) CISO expiré(s)
          </div>
          <p className="text-xs text-muted-foreground mt-1">Régénérez un lien d'activation one-time pour ces tenants.</p>
          <div className="mt-3 space-y-2">
            {expiredTenants.map((t) => (
              <div key={t.org_id} className="flex items-center justify-between gap-3 text-sm">
                <span>{t.name} <span className="font-mono text-xs text-muted-foreground">{t.org_id}</span></span>
                <Button size="sm" variant="outline" className="rounded-sm gap-1.5 border-red-500/40"
                  onClick={() => regen.mutate(t.org_id)} data-testid={`regen-expired-${t.org_id}`}>
                  <RefreshCw className="h-3.5 w-3.5" /> Régénérer
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : rows.length === 0 ? (
        <EmptyState icon={Network} title="Aucun tenant" />
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto" data-testid="tenants-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Tenant</th>
                <th className="text-left px-4 py-3">org_id</th>
                <th className="px-4 py-3">Utilisateurs</th>
                <th className="px-4 py-3">Actifs</th>
                <th className="px-4 py-3">Services</th>
                <th className="px-4 py-3">Statut secret</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const badge = SECRET_BADGE[t.ciso_secret_status] || SECRET_BADGE.none;
                return (
                  <tr key={t.org_id} className="border-t border-border" data-testid={`tenant-row-${t.org_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.sector}{t.country ? ` · ${t.country}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{t.org_id}</td>
                    <td className="px-4 py-3 text-center">{t.counts?.users}</td>
                    <td className="px-4 py-3 text-center">{t.counts?.assets}</td>
                    <td className="px-4 py-3 text-center">{t.counts?.services}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm border text-[11px] ${badge.cls}`}
                        data-testid={`tenant-secret-status-${t.org_id}`}>{badge.label}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-stretch gap-1.5">
                        {(t.ciso_secret_status === "pending" || t.ciso_secret_status === "expired") && (
                          <Button size="sm" variant="outline" className="rounded-sm gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
                            onClick={() => resend.mutate(t.org_id)} disabled={resend.isPending}
                            data-testid={`resend-activation-${t.org_id}`}>
                            <LinkIcon className="h-3.5 w-3.5" /> Renvoyer le lien
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-sm gap-1.5"
                          onClick={() => { if (window.confirm(`Régénérer les identifiants temporaires de ${t.name} ? L'ancien secret ET l'ancien lien seront invalidés.`)) regen.mutate(t.org_id); }}
                          data-testid={`regen-secret-${t.org_id}`}>
                          <RefreshCw className="h-3.5 w-3.5" /> Régénérer identifiants
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Journal des provisions (P1) avec filtres */}
      <div className="mt-8 border border-border rounded-sm p-6" data-testid="provisioning-journal-panel">
        <h3 className="font-head font-semibold text-lg flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-primary" /> Journal des provisions
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Opérations plateforme sensibles (provisioning, lien/secret, activation CISO, purge, échecs). Aucun secret n'est journalisé.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="journal-filters">
          <select value={jf.org_id} onChange={(e) => setJf({ ...jf, org_id: e.target.value })} className={SELECT_CLS} data-testid="journal-filter-tenant">
            <option value="">Tous les tenants</option>
            {rows.map((t) => <option key={t.org_id} value={t.org_id}>{t.name}</option>)}
          </select>
          <select value={jf.action} onChange={(e) => setJf({ ...jf, action: e.target.value })} className={SELECT_CLS} data-testid="journal-filter-action">
            {JOURNAL_ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={jf.result} onChange={(e) => setJf({ ...jf, result: e.target.value })} className={SELECT_CLS} data-testid="journal-filter-result">
            <option value="">Tous résultats</option>
            <option value="success">Succès</option>
            <option value="failure">Échec</option>
          </select>
          <input type="date" value={jf.date_from} onChange={(e) => setJf({ ...jf, date_from: e.target.value })} className={SELECT_CLS} data-testid="journal-filter-from" />
          <input type="date" value={jf.date_to} onChange={(e) => setJf({ ...jf, date_to: e.target.value })} className={SELECT_CLS} data-testid="journal-filter-to" />
          <Button size="sm" variant="ghost" className="rounded-sm text-xs"
            onClick={() => setJf({ org_id: "", action: "", result: "", date_from: "", date_to: "" })} data-testid="journal-filter-reset">Réinitialiser</Button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="journal-table">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Horodatage</th>
                <th className="text-left px-3 py-2">Acteur</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Cible</th>
                <th className="px-3 py-2">Résultat</th>
                <th className="text-left px-3 py-2">Correlation ID</th>
              </tr>
            </thead>
            <tbody>
              {journal.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">Aucune opération pour ces filtres.</td></tr>
              ) : journal.map((j) => (
                <tr key={j.seq} className="border-t border-border" data-testid={`journal-row-${j.seq}`}>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{j.ts ? new Date(j.ts).toLocaleString("fr-FR") : "—"}</td>
                  <td className="px-3 py-2 text-xs">{j.actor}</td>
                  <td className="px-3 py-2 text-xs">
                    {j.action_label}
                    {j.detail?.tenant_name ? ` · ${j.detail.tenant_name}` : ""}
                    {j.detail?.reason ? ` (${j.detail.reason})` : ""}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{j.org_id || j.target}</td>
                  <td className="px-3 py-2 text-center" data-testid={`journal-result-${j.seq}`}>
                    {j.result === "failure"
                      ? <span className="text-red-400 inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" /> échec</span>
                      : <span className="text-green-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> succès</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{j.correlation_id ? j.correlation_id.slice(0, 8) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Nettoyage sécurisé des organisations fantômes */}
      <div className="mt-8 border border-border rounded-sm p-6" data-testid="ghost-purge-panel">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-head font-semibold text-lg flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-red-400" /> Organisations fantômes (nettoyage sécurisé)
            </h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Nœuds « organisation » inertes de l'ancien CRUD (id ≠ org_id). Analysez d'abord (dry-run),
              puis confirmez explicitement. Ancres, tenants réservés et candidats référencés sont toujours exclus.
            </p>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm gap-2 shrink-0"
            onClick={() => scanGhosts.mutate()} disabled={scanGhosts.isPending} data-testid="scan-ghosts-button">
            <ShieldAlert className="h-4 w-4" /> Analyser (dry-run)
          </Button>
        </div>
        {ghosts && (
          <div className="mt-4" data-testid="ghosts-result">
            {ghosts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune organisation fantôme détectée.</p>
            ) : (
              <>
                <div className="space-y-2">
                  {ghosts.map((g) => (
                    <label key={g.id}
                      className={`flex items-center gap-3 p-3 border rounded-sm ${g.purgeable ? "border-border cursor-pointer hover:bg-secondary/30" : "border-orange-500/30 bg-orange-500/5"}`}
                      data-testid={`ghost-item-${g.id}`}>
                      <input type="checkbox" disabled={!g.purgeable} checked={!!selected[g.id]}
                        onChange={(e) => setSelected({ ...selected, [g.id]: e.target.checked })}
                        data-testid={`ghost-check-${g.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">
                          {g.name || "(sans nom)"} <span className="font-mono text-xs text-muted-foreground">{g.id.slice(0, 8)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          tenant {g.tenant_org_id} · réfs: edges {g.references.edges}, users {g.references.users}, nodes {g.references.nodes_scoped}
                        </div>
                      </div>
                      {!g.purgeable && <span className="text-[11px] text-orange-400 shrink-0">Référencé / réservé — non purgeable</span>}
                    </label>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <Button size="sm" variant="destructive" className="rounded-sm gap-2"
                    disabled={selectedIds.length === 0 || purge.isPending}
                    onClick={() => { if (window.confirm(`Purger définitivement ${selectedIds.length} organisation(s) fantôme(s) ? Action auditée.`)) purge.mutate(selectedIds); }}
                    data-testid="confirm-purge-button">
                    <Trash2 className="h-4 w-4" /> Confirmer la purge ({selectedIds.length})
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Dialogue de provisioning */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="provision-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Provisionner un tenant</DialogTitle>
            <DialogDescription>
              Crée un tenant isolé (organisation-ancre) + son CISO initial. Un lien d'activation one-time (et un secret de compatibilité) seront affichés une seule fois.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Nom du tenant *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="tenant-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Secteur</Label><Input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Pays</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 rounded-sm" /></div>
            </div>
            <div>
              <Label className="text-xs">Email du CISO initial *</Label>
              <Input type="email" value={form.ciso_email} onChange={(e) => setForm({ ...form, ciso_email: e.target.value })} className="mt-1 rounded-sm" data-testid="ciso-email-input" />
            </div>
            <div>
              <Label className="text-xs">Nom du CISO</Label>
              <Input value={form.ciso_name} onChange={(e) => setForm({ ...form, ciso_name: e.target.value })} className="mt-1 rounded-sm" data-testid="ciso-name-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => provision.mutate()} disabled={!form.name || !form.ciso_email || provision.isPending} data-testid="submit-provision-button">Provisionner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SecretDialog data={secret} onClose={() => setSecret(null)} />
    </AppShell>
  );
}
